import { useState } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { SearchableSelect } from "../ui/SearchableSelect";
import { AlertCircle, CheckCircle } from "lucide-react";
import { enrollmentService } from "../../services/enrollmentService";

interface UpdateStatusDialogProps {
  isOpen: boolean;
  onClose: () => void;
  enrollmentIds: string[];
  onSuccess: () => void;
}

export function UpdateStatusDialog({
  isOpen,
  onClose,
  enrollmentIds,
  onSuccess,
}: UpdateStatusDialogProps) {
  const [status, setStatus] = useState<string>("active");
  const [isUpdating, setIsUpdating] = useState(false);
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
    { value: "expired", label: "Expired" },
    { value: "cancelled", label: "Cancelled" },
    { value: "suspended", label: "Suspended" },
  ];

  const handleUpdateStatus = async () => {
    if (enrollmentIds.length === 0) return;

    setIsUpdating(true);
    setNotification(null);
    setProgress({ current: 0, total: enrollmentIds.length });

    let successCount = 0;
    let failureCount = 0;
    const errors: string[] = [];

    for (let i = 0; i < enrollmentIds.length; i++) {
      const id = enrollmentIds[i];
      if (!id) {
        failureCount++;
        continue;
      }

      try {
        await enrollmentService.updateEnrollment(id, { status });
        successCount++;
      } catch (error: any) {
        failureCount++;
        errors.push(`ID ${id}: ${error.message || "Failed"}`);
      }
      setProgress({ current: i + 1, total: enrollmentIds.length });
    }

    if (failureCount === 0) {
      setNotification({
        type: "success",
        message: `Successfully updated status for ${successCount} enrollment(s)`,
      });
      setTimeout(() => {
        onSuccess();
        onClose();
        setNotification(null);
      }, 1500);
    } else {
      setNotification({
        type: "error",
        message: `Updated ${successCount} enrollment(s), ${failureCount} failed`,
      });
    }

    setIsUpdating(false);
  };

  const handleReset = () => {
    setStatus("active");
    setNotification(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isUpdating) onClose();
      }}
      title="Update Enrollment Status"
    >
      <div className="space-y-6 py-2">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            You are about to update the status for{" "}
            <span className="font-bold">{enrollmentIds.length}</span> enrollment(s).
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            New Status
          </label>
          <SearchableSelect
            options={statusOptions}
            value={status}
            onChange={setStatus}
            placeholder="Select status..."
          />
        </div>

        {isUpdating && progress.total > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Updating enrollments...
              </span>
              <span className="text-sm text-gray-600">
                {progress.current} / {progress.total}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${(progress.current / progress.total) * 100}%`,
                }}
              />
            </div>
          </div>
        )}

        {notification && (
          <div
            className={`flex items-center gap-2 p-3 rounded-lg ${
              notification.type === "success"
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-red-50 text-red-700 border border-red-200"
            }`}
          >
            {notification.type === "success" ? (
              <CheckCircle size={18} />
            ) : (
              <AlertCircle size={18} />
            )}
            <span className="text-sm">{notification.message}</span>
          </div>
        )}

        <div className="flex justify-between gap-3 pt-6 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={handleReset}
            className="px-6 text-red-600 hover:bg-red-50 hover:border-red-100"
            disabled={isUpdating}
          >
            Reset
          </Button>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="px-6"
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateStatus}
              className="px-8 bg-[#102040] hover:bg-[#081a30]"
              disabled={isUpdating || enrollmentIds.length === 0}
            >
              {isUpdating ? "Updating..." : "Update Status"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
